// OTP approval sheet (spec §9.3): six boxes, 180-second countdown, then
// "OTP expired" and a Resend OTP that really sends a new code (§17.8).
import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { ApiError } from '@/api/client';
import OtpInput from '@/components/ui/OtpInput/OtpInput';
import Button from '@/components/ui/Button/Button';
import './auth.css';

export interface OtpSheetProps {
  show: boolean;
  /** "Approve adding RAHUL as a beneficiary" — omitted for delete (§4.2). */
  approvalLine?: string;
  /** Where the code went, e.g. "+91 98xxx xx210". */
  destination?: string;
  /** Bumps when a new OTP session starts; resets the countdown. */
  sessionKey?: number;
  expiresInSeconds?: number;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<number>;
  onClose: () => void;
}

const DEFAULT_SECONDS = 180;

const OtpSheet = ({
  show,
  approvalLine,
  destination,
  sessionKey = 0,
  expiresInSeconds = DEFAULT_SECONDS,
  onVerify,
  onResend,
  onClose,
}: OtpSheetProps) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(expiresInSeconds);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const startCountdown = useCallback((seconds: number) => {
    stopTimer();
    setSecondsLeft(seconds);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          stopTimer();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    if (show) {
      setCode('');
      setError('');
      startCountdown(expiresInSeconds);
    } else {
      stopTimer();
    }
    return stopTimer;
  }, [show, sessionKey, expiresInSeconds, startCountdown]);

  const expired = secondsLeft <= 0;

  const handleVerify = async (value: string) => {
    if (verifying) return;
    setError('');
    setVerifying(true);
    try {
      await onVerify(value);
      // Parent closes the sheet on success.
    } catch (err) {
      setCode('');
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResending(true);
    try {
      const seconds = await onResend();
      setCode('');
      startCountdown(seconds || DEFAULT_SECONDS);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not resend the OTP. Try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" keyboard={false} className="nova-auth-modal">
      <Modal.Header closeButton>
        <Modal.Title>Enter OTP</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {approvalLine && <p className="nova-auth-approval">{approvalLine}</p>}
        <p className="nova-auth-subtext">
          Code sent to {destination || 'your registered mobile'}
        </p>
        <OtpInput
          value={code}
          onChange={(v) => {
            setCode(v);
            setError('');
            if (v.length === 6) void handleVerify(v);
          }}
          hasError={Boolean(error)}
          disabled={verifying || expired}
        />
        {error && <p className="nova-auth-error">{error}</p>}
        <div className="nova-auth-timer">
          {expired ? (
            <>
              <span className="nova-auth-expired">OTP expired</span>
              <button type="button" className="nova-auth-resend" onClick={handleResend} disabled={resending}>
                {resending ? 'Sending…' : 'Resend OTP'}
              </button>
            </>
          ) : (
            <span>Resend in {secondsLeft} seconds</span>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => void handleVerify(code)} disabled={code.length !== 6 || expired} isLoading={verifying}>
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OtpSheet;
